/* BlueCollarPicker.jsx — SKILLED WORK & EARNING PATHS (city-first catalogue).
 *
 * Data: GET /api/blue-collar/browse  (publishable === true records only)
 * Flow: State → City → Popular platforms → Category → Career cards →
 *       Career details → EXISTING lock confirmation (unchanged) → official route.
 *
 * The commit-and-lock funnel is untouched:
 *   commit-category (irreversible) happens on the FINAL career confirmation,
 *   keep/unkeep up to 3 inside the committed category, commit-picks (final),
 *   preview_locked shows only kept picks. Server enforces every gate (423).
 */
import React, { useEffect, useMemo, useState } from 'react';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, ChevronLeft, Check, ArrowRight, AlertTriangle, RotateCcw,
  ShieldCheck, Info, MapPin, Search, ExternalLink, X, BadgeCheck,
  Sparkles, SprayCan, Wrench, Hammer, Truck, Car, HeartHandshake, Bug,
  Briefcase, Stethoscope, Shield, ConciergeBell, HardHat, Store, Plane,
  RadioTower, GraduationCap,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { CompanyRoutesSection } from '../components/bluecollar/CompanyRoutesSection';
import { StateSummary, StateCountPills, StateCover } from '../components/bluecollar/StateSummary';
import { useAuth } from '../lib/auth';
import SpecIntroVideo, { introSeen, markIntroSeen } from '../components/SpecIntroVideo';
import { getIntroVideo } from '../data/introVideos';

const INTRO_KEY = 'blue_collar';
const API = process.env.REACT_APP_BACKEND_URL;
// Option (a) 2026-09-02: legacy AP/TS career catalogue hidden — v1.2.0 JSON is the only visible source.
// Lock funnel code is intact; it simply has no cards to pick from.
const SHOW_LEGACY_CATALOGUE = false;

const ICONS = {
  Sparkles, SprayCan, Wrench, Hammer, Truck, Car, HeartHandshake, Bug,
  Briefcase, Stethoscope, Shield, ConciergeBell, HardHat, Store, Plane,
  RadioTower, GraduationCap,
};

const OPP_TYPES = ['Job', 'Partner work', 'Training', 'Apprenticeship'];

const EARNINGS_WARNING =
  'Earnings vary by city, hours, skill, incentives, ratings, commissions and work-related costs. Confirm the current offer on the official company page.';

function ConfirmModal({ open, title, body, cta, ctaTone = 'amber', onConfirm, onCancel, busy }) {
  if (!open) return null;
  const tone = ctaTone === 'red' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700';
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="confirm-modal">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-2 text-rose-600 mb-3">
          <AlertTriangle size={18} />
          <span className="text-[11px] font-extrabold tracking-widest uppercase">Final · cannot be undone</span>
        </div>
        <h2 className="text-xl font-extrabold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-700 leading-relaxed">{body}</p>
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-4 py-2.5 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-bold"
            data-testid="confirm-cancel">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={busy}
            className={`${tone} text-white px-5 py-2.5 rounded-full text-sm font-extrabold disabled:opacity-50 inline-flex items-center gap-1.5`}
            data-testid="confirm-go">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {cta}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function CompactKeptCard({ career }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="relative bg-white border border-amber-200 rounded-2xl p-5 hover:shadow-md hover:border-amber-400 transition-all"
      data-testid={`bc-kept-card-${career.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">
        {career.category} · via {career.platform || 'platform'}
      </div>
      <h3 className="font-bold text-gray-900 text-base leading-tight mb-1">{career.title}</h3>
      {career.headline && <div className="text-xs text-gray-500 mb-4 line-clamp-2">{career.headline}</div>}
      <Link to={`/blue-collar/preview/${career.id}`}
        className="inline-flex items-center text-sm font-semibold text-amber-700 hover:text-amber-900"
        data-testid={`bc-open-detail-${career.id}`}>
        Open full preview <ArrowRight size={14} className="ml-1" />
      </Link>
    </motion.div>
  );
}

/* ── Location selector: State (28, JSON stateOrder) → searchable City ── */
function LocationSelector({ stateList, loc, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const current = stateList.find((s) => s.stateName === loc.state);
  const cities = current?.cities || [];
  const filtered = cities.filter((c) => c.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="bg-white border-2 border-amber-200 rounded-3xl p-5 md:p-6 max-w-3xl mx-auto shadow-sm" data-testid="bc-location-selector">
      <p className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700 mb-3 flex items-center gap-1.5">
        <MapPin size={13} /> Step 1 — Where are you?
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={loc.state || ''} onValueChange={(st) => { onChange({ state: st, city: '' }); setQuery(''); }}>
          <SelectTrigger className="sm:w-60 min-h-[44px] rounded-full border-2 border-amber-400 text-sm font-bold text-amber-900 bg-white focus:ring-amber-500" data-testid="bc-state-select">
            <SelectValue placeholder="Select your state" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {stateList.map((s) => (
              <SelectItem key={s.stateCode} value={s.stateName} data-testid={`bc-state-option-${s.stateCode.toLowerCase()}`}>
                {s.stateName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <label htmlFor="bc-city-input" className="sr-only">Search your city or town</label>
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input id="bc-city-input" type="text" autoComplete="off" disabled={!loc.state}
            aria-label={loc.city ? `Selected city: ${loc.city}. Search to change city` : 'Search your city or nearest city'}
            placeholder={loc.city || (loc.state ? 'Search your city or nearest city…' : 'Select a state first')}
            value={query}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            className={`w-full min-h-[44px] pl-10 pr-4 py-2.5 rounded-full border-2 text-sm font-semibold focus:outline-none focus:border-amber-500 disabled:bg-gray-50 ${loc.city ? 'border-amber-400 text-amber-900 placeholder-amber-800' : 'border-gray-200 text-gray-800'}`}
            data-testid="bc-city-input" />
          {open && loc.state && (
            <ul className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-2xl shadow-lg max-h-56 overflow-auto py-1" role="listbox">
              {filtered.length === 0 && (
                <li className="px-4 py-2.5 text-sm text-gray-500">No listed city matches. Choose the nearest listed city.</li>
              )}
              {filtered.map((c) => (
                <li key={c}>
                  <button type="button" role="option" aria-selected={loc.city === c}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onChange({ ...loc, city: c }); setQuery(''); setOpen(false); }}
                    className="w-full text-left px-4 py-2.5 min-h-[44px] text-sm font-semibold text-gray-800 hover:bg-amber-50 focus-visible:bg-amber-50"
                    data-testid={`bc-city-option-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                    {c}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {loc.city && (
        <p className="mt-3 text-xs text-gray-500">
          Showing verified official routes for <strong className="text-gray-800">{loc.city}, {loc.state}</strong>. You can change city anytime before your final selection.
        </p>
      )}
    </div>
  );
}

/* ── Career picture card ────────────────────────────────────────────── */
function CareerCard({ c, city, kept, onDetails }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-3xl overflow-hidden border-2 transition-all flex flex-col ${kept ? 'border-amber-500 shadow-md' : 'border-gray-100 hover:border-amber-300 hover:shadow-lg'}`}
      data-testid={`bc-card-${c.career_id}`}>
      <div className="relative aspect-[3/2] bg-amber-50">
        <img src={c.image} alt={c.image_alt} loading="lazy"
          className="w-full h-full object-cover" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 text-emerald-800 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
          <BadgeCheck size={12} aria-hidden="true" /> Verified route
        </span>
        {kept && (
          <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-amber-600 text-white flex items-center justify-center shadow" aria-label="Kept">
            <Check size={15} />
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">{c.platform}</div>
        <h3 className="font-bold text-gray-900 text-base leading-snug">{c.title}</h3>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <MapPin size={11} aria-hidden="true" />
          {c.statewide ? (c.location_note || 'AP & Telangana') : (city || c.cities[0])}
          {!c.statewide && c.location_note ? ` · ${c.location_note}` : ''}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <span className="text-[10px] font-bold uppercase tracking-wide bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-full">{c.opportunity}</span>
          {c.training_available && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full">Learning route</span>
          )}
        </div>
        {c.entry && <p className="text-xs text-gray-600 mt-2.5 line-clamp-2">{c.entry}</p>}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <span className="text-[10px] text-gray-400 font-semibold">Checked {c.checked}</span>
          <button type="button" onClick={() => onDetails(c)}
            className="inline-flex items-center gap-1 min-h-[44px] px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-extrabold transition-colors focus-visible:ring-2 focus-visible:ring-amber-500"
            data-testid={`bc-see-details-${c.career_id}`}>
            See career details <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Full career detail overlay ─────────────────────────────────────── */
function DetailSection({ title, children }) {
  if (!children) return null;
  return (
    <section className="mb-5">
      <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700 mb-1.5">{title}</h3>
      <div className="text-sm text-gray-800 leading-relaxed">{children}</div>
    </section>
  );
}

function CareerDetailOverlay({ careerId, city, kept, keptCount, max, phase, busy, onClose, onSelect, onKeep, onUnkeep }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setD(null); setErr('');
    axios.get(`${API}/api/blue-collar/browse/${careerId}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not load this career.'));
  }, [careerId]);

  const pv = d?.platformVerification || {};
  const canKeepMore = keptCount < max;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="bc-detail-view">
      <div className="min-h-full flex items-start justify-center p-3 md:p-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden">
          {!d && !err && (
            <div className="p-16 text-center"><Loader2 className="w-7 h-7 animate-spin text-amber-600 mx-auto" /></div>
          )}
          {err && (
            <div className="p-10 text-center">
              <p className="text-sm text-rose-700 mb-4">{String(err)}</p>
              <Button onClick={onClose} variant="outline">Close</Button>
            </div>
          )}
          {d && (
            <>
              <div className="relative aspect-[3/1.4] bg-amber-100">
                <img src={d.image} alt={d.imageAlt || d.title} className="w-full h-full object-cover" />
                <button type="button" onClick={onClose} aria-label="Close career details"
                  className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/95 text-gray-700 flex items-center justify-center shadow hover:bg-white focus-visible:ring-2 focus-visible:ring-amber-500"
                  data-testid="bc-detail-close">
                  <X size={18} />
                </button>
                <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-white/95 text-emerald-800 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
                  <BadgeCheck size={12} /> Verified route · checked {pv.checked}
                </span>
              </div>
              <div className="p-5 md:p-7">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                  {d.category_label} · via {d.platform}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">{d.title}</h2>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin size={11} />
                  {d.statewide ? (d.location_note || 'AP & Telangana') : `${city || (d.cities || [])[0] || ''}`}
                  {!d.statewide && d.location_note ? ` · ${d.location_note}` : ''}
                  {' · '}{d.opportunity}
                </p>
                {d.respectMessage && <p className="text-sm text-teal-800 font-semibold mt-3">{d.respectMessage}</p>}

                <div className="mt-6">
                  <DetailSection title="What you will do">{d.dailyWork || d.description}</DetailSection>
                  <DetailSection title="Skills to build">
                    {Array.isArray(d.skillsToBuild) && d.skillsToBuild.length > 0 && (
                      <ul className="list-disc pl-5 space-y-0.5">{d.skillsToBuild.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    )}
                  </DetailSection>
                  <DetailSection title="Tools and products">
                    {Array.isArray(d.toolsAndProducts) && d.toolsAndProducts.length > 0 && (
                      <ul className="list-disc pl-5 space-y-0.5">{d.toolsAndProducts.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    )}
                  </DetailSection>
                  <DetailSection title="Eligibility & requirements">
                    {Array.isArray(d.verifiedRequirementsAndSchemes) && d.verifiedRequirementsAndSchemes.length > 0 && (
                      <ul className="list-disc pl-5 space-y-0.5">{d.verifiedRequirementsAndSchemes.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    )}
                  </DetailSection>
                  <DetailSection title="Training and certification">{d.trainingAndCertification}</DetailSection>
                  <DetailSection title="How to start">{d.howToStartOnPlatform}</DetailSection>

                  {(d.earningPatternIndia || d.earningSourcedNote) && (
                    <section className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="bc-detail-earnings">
                      <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-amber-800 mb-1.5">Earning pattern (indicative)</h3>
                      {d.earningPatternIndia && <p className="text-sm text-gray-800 leading-relaxed">{d.earningPatternIndia}</p>}
                      {d.earningSourcedNote && <p className="text-xs text-gray-600 mt-2">{d.earningSourcedNote}</p>}
                      <p className="text-xs text-amber-900 font-semibold mt-2 flex items-start gap-1.5">
                        <Info size={13} className="shrink-0 mt-0.5" /> {EARNINGS_WARNING}
                      </p>
                      {d.freshness && <p className="text-[11px] text-gray-500 mt-2">{d.freshness}</p>}
                    </section>
                  )}

                  <DetailSection title="Growth path">{d.growthPath}</DetailSection>
                  <DetailSection title="Independent business path">{d.independenceAndBusinessPath}</DetailSection>
                  <DetailSection title="AI advantage for this work">
                    <>
                      {d.ai_edge_for_this_work && <p className="mb-1.5">{d.ai_edge_for_this_work}</p>}
                      <p className="text-xs text-gray-500">
                        Hands-on work has lower direct automation exposure, while AI and digital tools can help workers learn, manage customers, improve productivity and grow earnings.
                      </p>
                    </>
                  </DetailSection>
                  <DetailSection title="Safety and professionalism">{d.safetyTrustAndProfessionalism}</DetailSection>

                  <section className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="bc-detail-verification">
                    <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-800 mb-1.5 flex items-center gap-1.5">
                      <ShieldCheck size={13} /> Verified platform information
                    </h3>
                    <p className="text-sm text-gray-800 font-semibold">{d.platform}</p>
                    {d.verification_status_label && <p className="text-xs text-emerald-800 font-bold mt-0.5">{d.verification_status_label}</p>}
                    <p className="text-xs text-gray-600 mt-1">{d.verificationTier}</p>
                    <p className="text-xs text-gray-500 mt-2">Last source check: <strong>{pv.checked}</strong></p>
                    {pv.url && (
                      <a href={pv.url} target="_blank" rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 min-h-[44px] px-5 py-2.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-extrabold transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500"
                        data-testid="bc-detail-official-link">
                        Open official application <ExternalLink size={14} />
                      </a>
                    )}
                  </section>
                </div>

                {/* Final selection — the EXISTING lock flow */}
                <div className="sticky bottom-0 -mx-5 md:-mx-7 -mb-5 md:-mb-7 bg-white/95 backdrop-blur border-t border-gray-200 px-5 md:px-7 py-4 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px] text-gray-500 max-w-[46%] hidden sm:block">
                    {phase === 'branch_pick'
                      ? 'Selecting a career locks your category — the final warning appears before anything is saved.'
                      : `You can keep up to ${max} careers in your locked category, then lock your picks.`}
                  </p>
                  {kept ? (
                    <button type="button" onClick={() => onUnkeep(careerId)} disabled={!!busy}
                      className="min-h-[44px] px-5 py-2.5 rounded-full border-2 border-amber-500 text-amber-800 bg-amber-50 text-sm font-extrabold inline-flex items-center gap-1.5"
                      data-testid="bc-detail-unkeep-btn">
                      <Check size={15} /> Kept · tap to remove
                    </button>
                  ) : (
                    <button type="button"
                      onClick={() => onSelect(d)}
                      disabled={!!busy || (phase === 'list_pick' && !canKeepMore)}
                      className="min-h-[44px] px-6 py-2.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-extrabold disabled:opacity-50 inline-flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-amber-500"
                      data-testid="bc-detail-select-btn">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                      {phase === 'branch_pick' ? 'Select this career' : (canKeepMore ? 'Keep this career' : `Limit of ${max} reached`)}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

/* ═════════════════════════ MAIN PAGE ═══════════════════════════════ */
export default function BlueCollarPicker() {
  const { user, refresh } = useAuth();

  const [state, setState] = useState(null);
  const [browse, setBrowse] = useState(null);
  const [routeStates, setRouteStates] = useState([]); // 28 states, JSON stateOrder
  const [stateSummaryRule, setStateSummaryRule] = useState('');
  const [sectionCovers, setSectionCovers] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [loc, setLocState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('bc_loc')) || { state: 'Telangana', city: '' }; }
    catch { return { state: 'Telangana', city: '' }; }
  });
  const setLoc = (v) => { setLocState(v); try { sessionStorage.setItem('bc_loc', JSON.stringify(v)); } catch { /* ignore */ } };

  const [platform, setPlatform] = useState(null);
  const [category, setCategory] = useState(null);
  const [oppType, setOppType] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const [selectConfirm, setSelectConfirm] = useState(null); // full career detail record
  const [picksConfirm, setPicksConfirm] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [busy, setBusy] = useState('');
  const [showIntro, setShowIntro] = useState(false);
  const [introChecked, setIntroChecked] = useState(false);

  useEffect(() => {
    setShowIntro(!introSeen(INTRO_KEY, user));
    setIntroChecked(true);
  }, [user]);

  const reload = async () => {
    try {
      const s = { data: { phase: 'branch_pick', branch_committed: false, kept_ids: [], previews: [], can_reset: false } };
      setState(s.data);
      if (s.data.phase === 'branch_pick' || s.data.phase === 'list_pick') {
        const [b, rs] = await Promise.all([
          axios.get(`${API}/api/blue-collar/browse`),
          axios.get(`${API}/api/blue-collar/company-routes/states`).catch(() => ({ data: { states: [] } })),
        ]);
        setBrowse(b.data);
        setRouteStates(rs.data.states || []);
        setStateSummaryRule(rs.data.stateSummaryRule || '');
        setSectionCovers(rs.data.sectionCovers || {});
      } else {
        setBrowse(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not load.');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Final selection from branch_pick: commit category + keep (EXISTING endpoints) */
  const doSelectCareer = async () => {
    if (!selectConfirm) return;
    setBusy('select'); setErr('');
    try {
      const catId = selectConfirm.__category_id;
      await axios.post(`${API}/api/blue-collar/commit-category`, { category: catId });
      await axios.post(`${API}/api/blue-collar/keep`, { career_id: String(selectConfirm.id) });
      setSelectConfirm(null); setDetailId(null);
      setCategory(null); setPlatform(null);
      await reload(); await refresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Could not save.'));
      setSelectConfirm(null);
    } finally { setBusy(''); }
  };

  const handleKeep = async (career) => {
    if (busy) return;
    setBusy('keep'); setErr('');
    try {
      await axios.post(`${API}/api/blue-collar/keep`, { career_id: String(career.id) });
      await reload(); await refresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Could not save.'));
    } finally { setBusy(''); }
  };

  const handleUnkeep = async (cid) => {
    if (busy) return;
    setBusy('unkeep');
    try {
      await axios.post(`${API}/api/blue-collar/unkeep`, { career_id: String(cid) });
      await reload(); await refresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Could not remove.'));
    } finally { setBusy(''); }
  };

  const doCommitPicks = async () => {
    setBusy('picks');
    try {
      await axios.post(`${API}/api/blue-collar/commit-picks`);
      setPicksConfirm(false);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Could not commit.'));
    } finally { setBusy(''); }
  };

  const doReset = async () => {
    setBusy('reset');
    try {
      await axios.post(`${API}/api/blue-collar/reset`);
      setResetConfirm(false);
      await reload(); await refresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Could not reset.'));
    } finally { setBusy(''); }
  };

  /* ── Filtering (publishable → city → platform → category → type → priority) ── */
  const careers = useMemo(() => browse?.careers || [], [browse]);
  // Merge: 28 JSON states (stateOrder preserved) + cities from the existing career catalogue.
  const stateList = useMemo(() => {
    const legacy = SHOW_LEGACY_CATALOGUE ? (browse?.states || {}) : {};
    const fromJson = routeStates.map((s) => ({
      ...s,
      cities: [...new Set([...(s.cities || []), ...(legacy[s.stateName] || [])])].sort(),
    }));
    const covered = new Set(fromJson.map((s) => s.stateName));
    const extra = Object.keys(legacy).filter((n) => !covered.has(n)).map((n) => ({ stateCode: n, stateName: n, cities: legacy[n] }));
    return [...fromJson, ...extra];
  }, [routeStates, browse]);
  const currentState = stateList.find((s) => s.stateName === loc.state);
  const legacyStateHasCareers = SHOW_LEGACY_CATALOGUE && !!(browse?.states || {})[loc.state];
  const cityCareers = useMemo(() => {
    if (!loc.city || !legacyStateHasCareers) return [];
    return careers
      .filter((c) => c.cities.includes(loc.city) || c.statewide)
      .sort((a, b) =>
        (a.statewide ? 1 : 0) - (b.statewide ? 1 : 0) ||
        a.familiar_rank - b.familiar_rank ||
        a.sort_order - b.sort_order);
  }, [careers, loc.city, legacyStateHasCareers]);

  const platformStats = useMemo(() => {
    const map = new Map();
    for (const c of cityCareers) {
      const s = map.get(c.platform) || { platform: c.platform, count: 0, types: new Set(), rank: c.familiar_rank };
      s.count += 1; s.types.add(c.opportunity);
      map.set(c.platform, s);
    }
    return [...map.values()].sort((a, b) => a.rank - b.rank || a.platform.localeCompare(b.platform));
  }, [cityCareers]);

  const categoryStats = useMemo(() => {
    const map = new Map();
    for (const c of cityCareers) {
      if (platform && c.platform !== platform) continue;
      const s = map.get(c.category_id) || { id: c.category_id, label: c.category_label, count: 0 };
      s.count += 1;
      map.set(c.category_id, s);
    }
    return [...map.values()];
  }, [cityCareers, platform]);

  const visibleCareers = useMemo(() => cityCareers
    .filter((c) => !platform || c.platform === platform)
    .filter((c) => !category || c.category_id === category)
    .filter((c) => !oppType || c.opportunity === oppType),
  [cityCareers, platform, category, oppType]);

  const oppTypesAvailable = useMemo(() => {
    const present = new Set(cityCareers.map((c) => c.opportunity));
    return OPP_TYPES.filter((t) => present.has(t));
  }, [cityCareers]);

  if (loading || !state || !introChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (showIntro) {
    const v = getIntroVideo(INTRO_KEY);
    return <SpecIntroVideo videoId={v.videoId} eyebrow={v.eyebrow}
      onDone={() => { markIntroSeen(INTRO_KEY, user); setShowIntro(false); }} />;
  }

  const phase = state.phase;
  const keptIds = state.kept_ids || [];
  const previews = state.previews || [];
  const MAX = state.max || 3;
  const canReset = state.can_reset;
  const importantNote = browse?.important_note || state.important_note || '';
  const iconFor = (catId) => {
    const meta = (browse?.categories || state.categories || []).find((x) => x.id === catId);
    return ICONS[meta?.icon] || Briefcase;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header row */}
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <Link to="/dashboard" className="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900" data-testid="bc-back-dashboard">
              <ChevronLeft size={16} className="mr-1" /> Dashboard
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => setShowIntro(true)}
                className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900"
                data-testid="bc-watch-intro-btn">▶ Watch intro</button>
              {state.branch_committed && (
                <span className="text-xs font-bold tracking-widest text-amber-700 inline-flex items-center gap-1.5" data-testid="bc-committed-banner">
                  <ShieldCheck size={13} /> COMMITTED · {state.committed_category_label || state.committed_category}
                </span>
              )}
              {canReset && (
                <button type="button" onClick={() => setResetConfirm(true)} disabled={!!busy}
                  className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900"
                  data-testid="bc-reset-btn">
                  <RotateCcw size={12} /> Start over ({state.resets_left ?? 1} left)
                </button>
              )}
              {state.reset_used && (
                <span className="text-[10px] uppercase tracking-widest text-gray-400" data-testid="bc-reset-used-label">
                  Both start-overs used · selections are final
                </span>
              )}
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">Skilled Work &amp; Earning Paths</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="bc-heading">
              {phase === 'preview_locked' ? 'Your locked picks' : 'Find verified work near you'}
            </h1>
            {phase !== 'preview_locked' && (
              <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
                Choose your city first. Then explore verified jobs, partner opportunities and learning routes available near you.
              </p>
            )}
            {phase === 'list_pick' && (
              <p className="text-xs text-amber-800 font-bold mt-2" data-testid="bc-scope-note">
                Category locked: {state.committed_category_label}. Keep up to {MAX} careers, then lock your picks.
              </p>
            )}
          </div>

          {/* Compact expandable earnings note (detailed warning lives on career details) */}
          {importantNote && phase !== 'preview_locked' && (
            <details className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm" data-testid="bc-important-banner">
              <summary className="cursor-pointer font-bold text-amber-900 text-xs uppercase tracking-widest flex items-center gap-2">
                <Info size={14} /> Earnings are indicative — tap to read why
              </summary>
              <p className="text-sm text-gray-800 leading-relaxed mt-2">{importantNote}</p>
            </details>
          )}

          {err && (
            <div className="max-w-2xl mx-auto mb-4 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl px-4 py-2" data-testid="bc-error">
              {String(err)}
            </div>
          )}

          <AnimatePresence mode="wait">
            {(phase === 'branch_pick' || phase === 'list_pick') && browse && (
              <motion.div key="catalogue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* 1 · Location */}
                <LocationSelector stateList={stateList} loc={loc}
                  onChange={(v) => { setLoc(v); setPlatform(null); setCategory(null); setOppType(null); }} />

                {!loc.state && (
                  <p className="text-center text-sm text-gray-500 mt-10" data-testid="bc-pick-city-prompt">
                    Pick your state above to see its verified official routes at a glance.
                  </p>
                )}

                {/* 2 · Four-layer state summary (from the state re-audit matrix) */}
                {loc.state && currentState?.coverImageUrl && <StateCover state={currentState} />}
                {loc.state && currentState?.counts && !loc.city && (
                  <StateSummary state={currentState} rule={stateSummaryRule} covers={sectionCovers}
                    onPickCity={(c) => { setLoc({ ...loc, city: c }); setPlatform(null); setCategory(null); setOppType(null); }} />
                )}
                {loc.state && currentState?.counts && loc.city && (
                  <div className="mt-6 flex justify-center" data-testid="bc-state-pills-compact"><StateCountPills counts={currentState.counts} /></div>
                )}
                {loc.state && !loc.city && currentState && !currentState.counts && (
                  <p className="text-center text-sm text-gray-500 mt-10">Now pick your city or nearest city.</p>
                )}

                {loc.city && (
                  <>
                    {/* 3 · Company-first official routes (skill-first → role previews → vehicle → manufacturing → safety) */}
                    <CompanyRoutesSection stateCode={currentState?.stateCode} city={loc.city} />

                    {/* 3 · Existing career catalogue → EXISTING lock flow (unchanged) */}
                    {cityCareers.length > 0 && (
                      <div className="mt-12 pt-8 border-t-2 border-dashed border-amber-200" data-testid="bc-career-catalogue">
                        <h2 className="text-lg md:text-xl font-extrabold text-gray-900 text-center">Pick your career path</h2>
                        <p className="text-xs text-gray-500 text-center mt-1 max-w-xl mx-auto">
                          Explore verified career routes below and select the one you want to commit to. {phase === 'branch_pick' ? 'Selecting a career locks your category.' : `Keep up to ${MAX} careers, then lock your picks.`}
                        </p>
                      </div>
                    )}

                    {/* 2 · Popular verified platforms */}
                    {platformStats.length > 0 && (
                      <div className="mt-10">
                        <h2 className="text-base md:text-lg font-extrabold text-gray-900 mb-3" data-testid="bc-platforms-heading">
                          Popular verified platforms in {loc.city}
                        </h2>
                        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                          {platformStats.map((p) => (
                            <button key={p.platform} type="button" aria-pressed={platform === p.platform}
                              onClick={() => setPlatform(platform === p.platform ? null : p.platform)}
                              className={`snap-start shrink-0 w-56 text-left rounded-2xl border-2 p-4 transition-all focus-visible:ring-2 focus-visible:ring-amber-500 ${
                                platform === p.platform ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-gray-200 bg-white hover:border-amber-300'
                              }`}
                              data-testid={`bc-platform-card-${p.platform.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-extrabold text-gray-900 text-sm leading-tight">{p.platform}</span>
                                <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full shrink-0">
                                  <BadgeCheck size={10} /> Verified source
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-500">{loc.city}</p>
                              <p className="text-xs text-gray-700 font-semibold mt-1.5">
                                {p.count} verified career route{p.count !== 1 ? 's' : ''}
                              </p>
                              <p className="text-[11px] text-gray-500">{[...p.types].join(' · ')}</p>
                              <span className="mt-2 inline-flex items-center gap-1 text-xs font-extrabold text-amber-700">
                                {platform === p.platform ? 'Showing opportunities ✓' : 'View opportunities'} <ArrowRight size={11} />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 3 · Categories */}
                    {categoryStats.length > 0 && (
                      <div className="mt-8">
                        <h2 className="text-base md:text-lg font-extrabold text-gray-900 mb-3">Categories</h2>
                        <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 lg:flex-wrap" data-testid="bc-categories">
                          {categoryStats.map((cat) => {
                            const Icon = iconFor(cat.id);
                            const active = category === cat.id;
                            return (
                              <button key={cat.id} type="button" aria-pressed={active}
                                onClick={() => setCategory(active ? null : cat.id)}
                                className={`shrink-0 inline-flex items-center gap-2 min-h-[44px] px-4 py-2.5 rounded-full border-2 text-sm font-bold transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 ${
                                  active ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white border-amber-100 text-gray-800 hover:border-amber-400'
                                }`}
                                data-testid={`bc-cat-${cat.id}`}>
                                <span className={`w-7 h-7 rounded-xl flex items-center justify-center ${active ? 'bg-white/20 text-white' : 'bg-gradient-to-br from-amber-500 to-orange-600 text-white'}`}>
                                  <Icon size={14} />
                                </span>
                                {cat.label}
                                <span className={`text-[10px] font-extrabold ${active ? 'text-amber-100' : 'text-amber-700'}`}>
                                  {cat.count} verified
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 4 · Opportunity type */}
                    {oppTypesAvailable.length > 1 && (
                      <div className="mt-4 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="group" aria-label="Opportunity type">
                        {oppTypesAvailable.map((t) => (
                          <button key={t} type="button" aria-pressed={oppType === t}
                            onClick={() => setOppType(oppType === t ? null : t)}
                            className={`shrink-0 min-h-[44px] px-4 py-2 rounded-full border text-xs font-bold transition-colors focus-visible:ring-2 focus-visible:ring-teal-500 ${
                              oppType === t ? 'bg-teal-700 border-teal-700 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-teal-400'
                            }`}
                            data-testid={`bc-opp-chip-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 5 · Career cards */}
                    {cityCareers.length > 0 && (
                    <div className="mt-8">
                      {visibleCareers.length === 0 ? (
                        <div className="text-center py-14 max-w-md mx-auto" data-testid="bc-empty-msg">
                          <MapPin size={28} className="mx-auto text-amber-400 mb-3" />
                          <p className="text-sm text-gray-700 font-semibold">
                            No career route matches these filters. Clear a filter or choose another nearby listed city.
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="bc-careers-grid">
                          {visibleCareers.map((c) => (
                            <CareerCard key={c.career_id} c={c} city={loc.city}
                              kept={keptIds.includes(c.career_id)}
                              onDetails={(card) => setDetailId(card.career_id)} />
                          ))}
                        </div>
                      )}
                    </div>
                    )}

                    {/* list_pick lock bar */}
                    {phase === 'list_pick' && cityCareers.length > 0 && (
                      <div className="sticky bottom-4 mt-8 max-w-xl mx-auto bg-white border-2 border-amber-300 rounded-2xl shadow-lg px-5 py-3.5 flex items-center justify-between gap-3" data-testid="bc-lock-bar">
                        <span className="text-sm font-bold text-gray-800">{keptIds.length} of {MAX} kept</span>
                        <Button onClick={() => setPicksConfirm(true)}
                          disabled={keptIds.length === 0 || busy === 'picks'}
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold min-h-[44px]"
                          data-testid="bc-commit-picks-btn">
                          Lock these {keptIds.length || ''} picks (final)
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {phase === 'preview_locked' && (
              <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="bc-preview-view">
                <p className="text-center text-sm text-gray-600 mb-6 max-w-xl mx-auto">
                  Your final picks. Tap any card to open the full preview — one at a time, every section, exactly as in the admin view.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                  {previews.map((p) => <CompactKeptCard key={p.id} career={p} />)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Career detail overlay */}
      {detailId && (
        <CareerDetailOverlay
          careerId={detailId}
          city={loc.city}
          kept={keptIds.includes(String(detailId))}
          keptCount={keptIds.length}
          max={MAX}
          phase={phase}
          busy={busy}
          onClose={() => setDetailId(null)}
          onSelect={(d) => {
            const card = careers.find((x) => x.career_id === String(d.id));
            const rec = { ...d, __category_id: card?.category_id };
            if (phase === 'branch_pick') setSelectConfirm(rec);
            else handleKeep(d);
          }}
          onKeep={handleKeep}
          onUnkeep={handleUnkeep}
        />
      )}

      {/* EXISTING lock confirmations — the "final" warning lives here now */}
      <ConfirmModal
        open={!!selectConfirm}
        title={`Select ${selectConfirm?.title || ''}?`}
        body={`⚠ Each choice is final. This locks your category to ${selectConfirm?.category_label || 'this category'} — every other category becomes unreachable, and you cannot go back to roam the catalogue. You can still keep up to ${MAX} careers inside this category before locking your final picks. Continue?`}
        cta="Yes, select this career"
        onConfirm={doSelectCareer}
        onCancel={() => setSelectConfirm(null)}
        busy={busy === 'select'}
      />
      <ConfirmModal
        open={picksConfirm}
        title={`Lock these ${keptIds.length} picks?`}
        body="⚠ Each choice is final. After this, you can't change your selections. The catalogue stays hidden. Only your kept previews will be visible. Continue?"
        cta="Yes, lock my picks"
        onConfirm={doCommitPicks}
        onCancel={() => setPicksConfirm(false)}
        busy={busy === 'picks'}
      />
      <ConfirmModal
        open={resetConfirm}
        title={`Start over? (${state.resets_left ?? 1} of 2 left)`}
        body={`You get 2 start-overs per path. This uses one — your current picks are safely archived as earlier explorations on your Clarity Card, and you can pick fresh previews. You have ${state.resets_left ?? 1} start-over${(state.resets_left ?? 1) === 1 ? '' : 's'} left. Continue?`}
        cta="Yes, start over"
        ctaTone="red"
        onConfirm={doReset}
        onCancel={() => setResetConfirm(false)}
        busy={busy === 'reset'}
      />
      <PathwayDisclaimer />
    </div>
  );
}
